import React from "react";
import { useFormik } from "formik";
import YupObject from "yup/lib/object";
import YupString from "yup/lib/string";

import TextField from "@mui/material/TextField";
import OutlinedInput from "@mui/material/OutlinedInput";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import DialogTitleWithClose from "./DialogTitleWithClose";
import SuccessFailedModal from "components/SuccessFailedModal";
import { Button } from "components/Buttons";

import { NUMBER_OF_EMPLOYEES_OPTIONS } from "constants/clinic";
import { inputStyle, selectStyle } from "../styles";
import { ClinicRepository } from "repositories/ClinicRepository";
import { ClinicType } from "types/Clinic";
import { ButtonColorScheme, ButtonType } from "types/ui";

const selectPlaceHolder = "Number of Employees";

const validationSchema = new YupObject().shape({
  name: new YupString().trim().required("Clinic name is required"),
  selectedNumberOfEmployees: new YupString().trim(),
  locationName: new YupString(),
});

const ClinicModalForm: React.FC<{
  isOpen: boolean;
  clinic?: ClinicType;
  closeModal: () => void;
  getList: () => void;
  addClinicAdmin?: (clinicId: string) => void;
}> = (props) => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  const [newClinicId, setNewClinicId] = React.useState("");

  const formik = useFormik({
    initialValues: {
      name: "",
      address: "",
      selectedNumberOfEmployees: "",
    },
    validationSchema,
    onSubmit: async (data, { resetForm, setFieldError }) => {
      try {
        const body = {
          name: data.name,
          address: data.address,
          numberOfEmployees: data.selectedNumberOfEmployees,
        };

        const clinicRepository = ClinicRepository.getInstance();
        if (!props.clinic) {
          const newClinic = await clinicRepository.createClinic(body);
          setNewClinicId(newClinic._id);
          setIsSuccessModalOpen(true);
        } else {
          await clinicRepository.updateClinic(props.clinic._id, body);
          clearData();
        }
        props.getList();
      } catch (err: any) {
        if (
          err.response.status === 400 &&
          err.response.data === "Clinic already exist"
        ) {
          setFieldError("name", err.response.data);
        } else {
          setIsError(true);
        }
      }
    },
  });

  const clearData = () => {
    props.closeModal();
    formik.resetForm();
    setNewClinicId("");
  };

  React.useEffect(() => {
    if (props.clinic) {
      formik.setValues({
        name: props.clinic.name,
        address: props.clinic.address,
        selectedNumberOfEmployees: props.clinic.numberOfEmployees,
      });
    }
  }, [props.clinic]);

  return (
    <>
      <Dialog
        open={props.isOpen}
        onClose={clearData}
        scroll="body"
        PaperProps={{
          sx: {
            width: "382px",
          },
        }}
      >
        <DialogTitleWithClose
          onModalClose={clearData}
          title={`${props.clinic ? "Update" : "Create New"} Clinic`}
        />

        <DialogContent sx={{ mt: "20px" }}>
          <Grid container>
            <TextField
              variant="standard"
              placeholder="Clinic Name"
              value={formik.values.name}
              fullWidth
              margin="normal"
              style={inputStyle}
              InputProps={{
                disableUnderline: true,
                style: {
                  color: "#5C5D68",
                },
              }}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="name"
              sx={{
                border:
                  formik.touched.name && formik.errors.name
                    ? "1px solid #DD3341 !important"
                    : "",
              }}
            />
            {formik.touched.name && formik.errors.name && (
              <FormHelperText
                error={Boolean(formik.touched.name && formik.errors.name)}
              >
                {formik.errors.name}
              </FormHelperText>
            )}

            <TextField
              size="small"
              variant="standard"
              placeholder="Clinic Location"
              value={formik.values.address}
              fullWidth
              required
              margin="normal"
              style={inputStyle}
              InputProps={{
                disableUnderline: true,
                style: {
                  color: "#5C5D68",
                },
              }}
              onChange={formik.handleChange}
              name="address"
            />

            <FormControl variant="standard" sx={{ width: "100%" }}>
              <Select
                variant="filled"
                value={formik.values.selectedNumberOfEmployees}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 165,
                      padding: 5,
                    },
                  },
                }}
                input={<OutlinedInput />}
                style={selectStyle}
                name="selectedNumberOfEmployees"
                sx={{
                  "& .MuiSelect-select .notranslate::after": selectPlaceHolder
                    ? {
                        content: `"${selectPlaceHolder}"`,
                        opacity: 0.42,
                      }
                    : {},
                  "&& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&& .MuiOutlinedInput-input:focus": {
                    background: "none",
                  },
                }}
              >
                {NUMBER_OF_EMPLOYEES_OPTIONS.map((item) => {
                  return (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ padding: "0px 24px 24px" }}>
          <Grid container wrap="nowrap" gap="24px">
            <Button
              title="Save"
              onClick={formik.handleSubmit}
              type={ButtonType.normal}
              colorScheme={
                formik.isSubmitting || Boolean(newClinicId)
                  ? ButtonColorScheme.disabled
                  : ButtonColorScheme.gradient
              }
              shadowed
              classNames="w-[150px]"
              disabled={formik.isSubmitting || Boolean(newClinicId)}
            />

            {!props.clinic && (
              <Button
                title="Add Clinic Admin"
                onClick={() => {
                  if (props.addClinicAdmin) {
                    props.addClinicAdmin(newClinicId);
                    setNewClinicId("");
                    formik.resetForm();
                  }
                }}
                type={ButtonType.normal}
                colorScheme={
                  newClinicId
                    ? ButtonColorScheme.gradient
                    : ButtonColorScheme.disabled
                }
                disabled={!newClinicId}
                shadowed
              />
            )}
          </Grid>
        </DialogActions>
      </Dialog>

      <SuccessFailedModal
        isError={isError}
        successMessage="New Clinic Created"
        failMessage="Failed to create clinic"
        open={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
          setIsError(false);
        }}
      />
    </>
  );
};

export default ClinicModalForm;
